import { type PlayerIndex, QUIZ_LENGTH, QUIZ_QUESTION_MS, type QuizState } from "@playpal/core";
import { useEffect, useState } from "react";

interface Props {
	state: QuizState;
	seat: PlayerIndex;
	onMove: (move: unknown) => void;
	/** Server clock minus this device's clock. */
	clockOffset: number;
}

const CATEGORY_LABEL = {
	maths: "🔢 Numbers",
	words: "🔤 Words",
	world: "🌍 The world",
} as const;

export function QuizBoard({ state, seat, onMove, clockOffset }: Props) {
	const question = state.questions[state.index];
	const myAnswer = state.answers[seat];
	const theirAnswer = state.answers[seat === 0 ? 1 : 0];
	const revealing = state.phase === "reveal";

	const remaining = useCountdown(state.endsAt, clockOffset, state.phase === "question");
	const fraction = Math.max(0, Math.min(1, remaining / QUIZ_QUESTION_MS));

	if (!question) return null;

	return (
		<div className="board-frame board-2xl">
			<div className="mb-3 flex items-center justify-between text-sm font-semibold text-indigo-900/70 dark:text-indigo-100/70">
				<span>
					Question {state.index + 1} of {QUIZ_LENGTH}
				</span>
				<span>{CATEGORY_LABEL[question.category]}</span>
			</div>

			<div
				className="mb-4 h-3 overflow-hidden rounded-full bg-white/60 dark:bg-white/10"
				role="timer"
				aria-label={`${Math.ceil(remaining / 1000)} seconds left`}
			>
				<div
					className={`h-full rounded-full transition-[width] duration-200 ease-linear ${
						fraction < 0.25 ? "bg-rose-500" : "bg-emerald-500"
					}`}
					style={{ width: `${revealing ? 100 : fraction * 100}%` }}
				/>
			</div>

			<h3 className="mb-5 text-center text-3xl font-black sm:text-4xl">{question.prompt}</h3>

			<div className="grid gap-3 sm:grid-cols-2">
				{question.choices.map((choice, index) => {
					const chosenByMe = myAnswer?.choice === index;
					const chosenByThem = revealing && theirAnswer?.choice === index;
					const correct = revealing && question.answer === index;
					const wrongPick = revealing && chosenByMe && !correct;

					return (
						<button
							key={choice}
							type="button"
							disabled={revealing || myAnswer !== null}
							onClick={() => onMove({ choice: index })}
							className={`rounded-2xl px-5 py-4 text-left text-2xl font-bold shadow-md transition ${
								correct
									? "bg-emerald-400 text-emerald-950"
									: wrongPick
										? "bg-rose-400 text-rose-950"
										: chosenByMe
											? "bg-indigo-600 text-white"
											: "bg-white/80 enabled:hover:scale-[1.02] dark:bg-white/10"
							}`}
						>
							<span>{choice}</span>
							{(chosenByMe || chosenByThem) && (
								<span className="ml-2 text-base font-semibold opacity-80">
									{chosenByMe ? "you" : ""}
									{chosenByMe && chosenByThem ? " + " : ""}
									{chosenByThem ? "them" : ""}
								</span>
							)}
						</button>
					);
				})}
			</div>

			<p className="mt-5 text-center text-lg font-semibold" aria-live="polite">
				{revealing
					? describeAward(state.lastAward[seat])
					: myAnswer
						? theirAnswer
							? "Both in!"
							: "Locked in — waiting for your friend…"
						: "Pick an answer, quick!"}
			</p>
		</div>
	);
}

function describeAward(points: number): string {
	if (points >= 2) return "Correct — and fastest! +2 points 🏆";
	if (points === 1) return "Correct! +1 point ✅";
	return "Not this time — next question coming up…";
}

/** Ticks four times a second against the server's clock, not the device's. */
function useCountdown(endsAt: number, clockOffset: number, running: boolean): number {
	const [remaining, setRemaining] = useState(() => endsAt - (Date.now() + clockOffset));

	useEffect(() => {
		if (!running) return;
		const update = () => setRemaining(endsAt - (Date.now() + clockOffset));
		update();
		const timer = setInterval(update, 250);
		return () => clearInterval(timer);
	}, [endsAt, clockOffset, running]);

	return Math.max(0, remaining);
}
